import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ManualEditAiHistory, ManualEditAiHistoryTurn, ManualEditAiResult } from '../../api/manualEditAi';
import { HomeChatHttpError, postCompanionRoute, type CompanionDocumentKind, type CompanionRouteRequest } from './api';
import { toUserFacingError } from '../../utils/userFacingError';
import { documentHistoryTurn } from './workflowDocumentHistory';

export interface DocumentAssistantCapability {
  key: string;
  flowId: string;
  documentLabel: string;
  documentKind: CompanionDocumentKind;
  stageCode: 202 | 206 | 603 | 622;
  activeEditor: boolean;
  canEdit: boolean;
  ready: boolean;
  submitEdit(request: string, guard?: { assertCurrent(): void }): Promise<ManualEditAiResult>;
  loadEditHistory(): Promise<ManualEditAiHistory>;
  openDocumentHistory(): void;
}
export type DocumentConversationTurn = {
  id: string; request: string; completedAt?: string; submittedBy?: string; documentLabel?: string;
  /** In-memory React identity across this request's optimistic/canonical result. */
  presentationKey?: string;
  result?: ManualEditAiResult; error?: string; pending?: boolean;
  clarification?: string; unknown?: boolean; reloadRequired?: boolean; intent?: 'edit' | 'question';
};
type SendOptions = {
  generalAuthorized: boolean; sessionId?: string | null; explicitIntent?: 'edit' | 'question';
  onConversation?: (sessionId: string, response: unknown) => Promise<void> | void;
  onSessionAllocated?: (sessionId: string) => void;
  continuation?: Pick<CompanionRouteRequest, 'regime' | 'final_question' | 'focus_token' | 'topic_focus' | 'prior_clarification_questions' | 'answer_mode'>;
};
type Scope = { user: string; flowId: string | null; stage: string; eligible: boolean; documentWorkflow?: boolean };
type Registered = { capability: DocumentAssistantCapability; registrationId: string };
type State = {
  registration: Registered | null; turns: DocumentConversationTurn[]; draft: string;
  documentMode: 'edit' | 'question' | null; explicitEdit: boolean; mutationPending: boolean; routing: boolean; historyWarning: string | null;
  unknownOutcome: boolean; reloadRequired: boolean; openRevision: number; flowId: string | null; scopeUser: string;
};
type Context = State & {
  register(capability: DocumentAssistantCapability, owner: object): () => void;
  update(capability: DocumentAssistantCapability, owner: object): void;
  setScope(scope: Scope): void; setDraft(value: string): void; setDocumentMode(mode: 'edit' | 'question'): void; setExplicitEdit(value: boolean): void;
  openAssistant(): void; allowNavigation(): boolean; allowDocumentViewChange(): boolean;
  sendMessage(request: string, options: SendOptions): Promise<void>;
  reloadHistory(): Promise<void>; acknowledgeUnknown(): void;
};
const initial: State = { registration: null, turns: [], draft: '', documentMode: null, explicitEdit: false, mutationPending: false, routing: false, historyWarning: null, unknownOutcome: false, reloadRequired: false, openRevision: 0, flowId: null, scopeUser: '' };
const fallback: Context = { ...initial, register: () => () => {}, update: () => {}, setScope: () => {}, setDraft: () => {}, setDocumentMode: () => {}, setExplicitEdit: () => {}, openAssistant: () => {}, allowNavigation: () => true, allowDocumentViewChange: () => true, sendMessage: async () => {}, reloadHistory: async () => {}, acknowledgeUnknown: () => {} };
const AssistantContext = createContext<Context>(fallback);
const id = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export function DocumentAssistantProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<State>(initial);
  const stateRef = useRef(state); stateRef.current = state;
  const registrationRef = useRef<(Registered & { owner: object }) | null>(null);
  const scopeRef = useRef<Scope>({ user: '', flowId: null, stage: '', eligible: true });
  const pendingRef = useRef(false);
  const pendingRecordRef = useRef<{ request: string; startedAt: string } | null>(null);
  const routingRef = useRef(false);
  const activeRequestRef = useRef<string | null>(null);
  const historyRequestRef = useRef(0);
  const scopeRevisionRef = useRef(0);
  const draftStorageKey = useRef<string | null>(null);
  const draftRevisions = useRef(new Map<string, object>());
  const draftGeneration = useRef(0);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const writeState = useCallback((update: Partial<State> | ((current: State) => State)) => {
    if (!mounted.current) return;
    setState(current => typeof update === 'function' ? update(current) : { ...current, ...update });
  }, []);
  const persistDraft = useCallback((draft: string, explicitEdit: boolean) => {
    if (!draftStorageKey.current) return;
    draftRevisions.current.set(draftStorageKey.current, {});
    try { sessionStorage.setItem(draftStorageKey.current, JSON.stringify({ version: 1, draft, explicitEdit, pending: pendingRecordRef.current })); } catch { /* browser storage is optional */ }
  }, []);
  const setDraft = useCallback((draft: string) => { persistDraft(draft, stateRef.current.explicitEdit); writeState({ draft }); }, [persistDraft, writeState]);
  const setDocumentMode = useCallback((documentMode: 'edit' | 'question') => writeState({ documentMode }), [writeState]);
  const setExplicitEdit = useCallback((explicitEdit: boolean) => { persistDraft(stateRef.current.draft, explicitEdit); writeState({ explicitEdit }); }, [persistDraft, writeState]);
  const reloadHistory = useCallback(async () => {
    const active = registrationRef.current;
    const scope = scopeRef.current;
    if (!active || scope.documentWorkflow === false
      || ((scope.flowId !== null || scope.documentWorkflow === true) && scope.flowId !== active.capability.flowId)) return;
    const requestId = ++historyRequestRef.current;
    const startingTurns = new Map(stateRef.current.turns.map(turn => [turn.id, turn]));
    const isCurrent = () => mounted.current && historyRequestRef.current === requestId
      && registrationRef.current?.registrationId === active.registrationId;
    try {
      const history = await active.capability.loadEditHistory();
      if (!isCurrent()) return;
      writeState(current => {
        if (!isCurrent()) return current;
        const currentTurns = new Map(current.turns.map(turn => [turn.id, turn]));
        const seen = new Set<string>();
        const turns = (Array.isArray(history?.turns) ? history.turns : []).flatMap(turn => {
          const canonical = documentHistoryTurn(turn, active.capability);
          if (seen.has(canonical.id)) return [];
          seen.add(canonical.id);
          const existing = currentTurns.get(canonical.id);
          // A completed edit may be newer than this snapshot, even with the same ID.
          return [existing && existing !== startingTurns.get(canonical.id) ? existing
            : { ...canonical, presentationKey: existing?.presentationKey }];
        });
        // Fresh reloads still replace old canonical history; only concurrent
        // updates and transient pending/error turns belong to the local tail.
        turns.push(...current.turns.filter(turn => !seen.has(turn.id)
          && (!turn.result?.history_turn || turn !== startingTurns.get(turn.id))));
        return { ...current, historyWarning: history?.truncated ? 'Older AI requests are available through document History.' : null, turns };
      });
    } catch {
      if (isCurrent()) writeState(current => isCurrent() ? { ...current, historyWarning: 'Unable to load earlier AI edit requests. You can still submit a new request.' } : current);
    }
  }, [writeState]);
  // Child editors register before App's parent layout effect establishes scope.
  // Hydrate each settled scope, including read-only eligibility transitions
  // that keep the same editor registered.
  const registrationId = state.registration?.registrationId;
  const scopeRevision = scopeRevisionRef.current;
  useEffect(() => {
    if (registrationId && registrationId === registrationRef.current?.registrationId) void reloadHistory();
  }, [registrationId, scopeRevision, reloadHistory]);
  const register = useCallback((capability: DocumentAssistantCapability, owner: object) => {
    const entry = { capability, owner, registrationId: id() };
    registrationRef.current = entry;
    let draft = ''; let explicitEdit = false;
    draftStorageKey.current = `farchat:document-assistant:v1:${scopeRef.current.user}:${capability.flowId}:${capability.key}`;
    if (!draftRevisions.current.has(draftStorageKey.current)) draftRevisions.current.set(draftStorageKey.current, {});
    try { const saved = JSON.parse(sessionStorage.getItem(draftStorageKey.current) || 'null'); if (saved?.version === 1) { draft = String(saved.draft || '').slice(0, 4000); explicitEdit = saved.explicitEdit === true; if (saved.pending?.request && saved.pending?.startedAt) { pendingRecordRef.current = saved.pending; pendingRef.current = true; writeState({ unknownOutcome: true, mutationPending: true }); } } } catch { /* ignore invalid legacy records */ }
    writeState(current => ({ ...current, registration: entry, turns: [], draft, documentMode: null, explicitEdit, historyWarning: null, flowId: capability.flowId }));
    return () => {
      if (registrationRef.current?.owner !== owner) return;
      registrationRef.current = null;
      draftStorageKey.current = null;
      writeState({ registration: null, documentMode: null, explicitEdit: false, turns: [], draft: '' });
    };
  }, [writeState]);
  const update = useCallback((capability: DocumentAssistantCapability, owner: object) => {
    const active = registrationRef.current;
    if (!active || active.owner !== owner) return;
    registrationRef.current = { ...active, capability };
    // Callback identities change after editor state updates; only publish capability flags.
    if (active.capability.ready !== capability.ready || active.capability.canEdit !== capability.canEdit || active.capability.activeEditor !== capability.activeEditor) {
      writeState({ registration: registrationRef.current });
    }
  }, [writeState]);
  const setScope = useCallback((scope: Scope) => {
    const previous = scopeRef.current;
    scopeRef.current = scope;
    if (previous.user !== scope.user || previous.flowId !== scope.flowId || previous.stage !== scope.stage
      || previous.eligible !== scope.eligible || previous.documentWorkflow !== scope.documentWorkflow) {
      historyRequestRef.current += 1;
      scopeRevisionRef.current += 1;
    }
    if (previous.user !== scope.user) {
      draftGeneration.current += 1;
      draftRevisions.current.clear();
      registrationRef.current = null;
      pendingRef.current = false;
      routingRef.current = false;
      activeRequestRef.current = null;
      pendingRecordRef.current = null;
      draftStorageKey.current = null;
      writeState({ scopeUser: scope.user, registration: null, turns: [], draft: '', documentMode: null, explicitEdit: false, mutationPending: false, unknownOutcome: false, reloadRequired: false, routing: false });
    }
    if (previous.user && previous.user !== scope.user) {
      try { for (const key of Object.keys(sessionStorage)) if (key.startsWith(`farchat:document-assistant:v1:${previous.user}:`)) sessionStorage.removeItem(key); } catch { /* best effort */ }
    }
    const active = registrationRef.current;
    if (active && previous.user !== scope.user) {
      draftStorageKey.current = `farchat:document-assistant:v1:${scope.user}:${active.capability.flowId}:${active.capability.key}`;
      let draft = ''; let explicitEdit = false;
      try { const saved = JSON.parse(sessionStorage.getItem(draftStorageKey.current) || 'null'); if (saved?.version === 1) { draft = String(saved.draft || '').slice(0, 4000); explicitEdit = saved.explicitEdit === true; if (saved.pending?.request && saved.pending?.startedAt) { pendingRecordRef.current = saved.pending; pendingRef.current = true; writeState({ unknownOutcome: true, mutationPending: true }); } } } catch { /* optional state */ }
      writeState({ draft, explicitEdit });
    }
    writeState({ flowId: scope.documentWorkflow === false ? null : scope.flowId });
  }, [writeState]);
  const allowNavigation = useCallback(() => !pendingRef.current, []);
  const allowDocumentViewChange = useCallback(() => !pendingRef.current && !routingRef.current, []);
  const openAssistant = useCallback(() => writeState(current => ({ ...current, openRevision: current.openRevision + 1, documentMode: 'edit', explicitEdit: true })), [writeState]);
  const sendMessage = useCallback(async (value: string, options: SendOptions) => {
    const request = value.trim();
    const active = registrationRef.current;
    if (!request || request.length > 4000 || pendingRef.current || routingRef.current || !active?.capability.activeEditor) return;
    const registrationId = active.registrationId;
    const capturedScope = { ...scopeRef.current };
    const submittedDraft = { key: draftStorageKey.current, revision: draftStorageKey.current ? draftRevisions.current.get(draftStorageKey.current) : undefined,
      generation: draftGeneration.current, value: stateRef.current.draft };
    const assertCurrent = () => {
      const current = registrationRef.current;
      const scope = scopeRef.current;
      if (!current || current.registrationId !== registrationId || !current.capability.activeEditor || !current.capability.canEdit || (!pendingRef.current && !current.capability.ready)
        || (scope.flowId && scope.flowId !== current.capability.flowId) || !scope.eligible
        || scope.user !== capturedScope.user || scope.stage !== capturedScope.stage) throw new Error('Open Manual Edit or Generate SOW/PWS to edit this document.');
    };
    const turnId = `pending-${id()}`;
    activeRequestRef.current = turnId;
    routingRef.current = true;
    writeState(current => ({ ...current, routing: true, turns: [...current.turns.filter(turn => !turn.clarification), { id: turnId, presentationKey: turnId, request, intent: options.explicitIntent, pending: true, completedAt: new Date().toISOString() }] }));
    try {
      let instruction: Awaited<ReturnType<typeof postCompanionRoute>>;
      if (options.explicitIntent === 'edit') {
        // A visible user selection is authoritative; the existing adapter still
        // flushes and verifies the current revision before its authorized write.
        assertCurrent();
        instruction = { kind: 'edit_current_document', message: request, editor_registration_id: registrationId, correlation_id: turnId };
      } else {
        instruction = await postCompanionRoute({ flow_id: active.capability.flowId, session_id: options.sessionId || null, message: request, client_request_id: turnId,
          editor_context: { registration_id: registrationId, document_kind: active.capability.documentKind }, explicit_intent: options.explicitIntent, ...options.continuation });
      }
      if (options.explicitIntent === 'question' && instruction.kind === 'edit_current_document') {
        throw new Error('A procurement question cannot change the document.');
      }
      if (registrationRef.current?.registrationId !== registrationId) return;
      if (instruction.kind === 'conversation') {
        writeState(current => ({ ...current, turns: current.turns.filter(turn => turn.id !== turnId) }));
        await options.onConversation?.(instruction.session_id, instruction.response);
        // Postprocessing may outlive navigation. Finalize only the unchanged
        // submitted draft, never the newly registered document's live draft.
        if (mounted.current && activeRequestRef.current === turnId && submittedDraft.generation === draftGeneration.current
          && submittedDraft.key && submittedDraft.value.trim() === request
          && draftRevisions.current.get(submittedDraft.key) === submittedDraft.revision) {
          try {
            const saved = JSON.parse(sessionStorage.getItem(submittedDraft.key) || 'null');
            if (saved?.version === 1 && saved.draft === submittedDraft.value) {
              sessionStorage.setItem(submittedDraft.key, JSON.stringify({ ...saved, draft: '' }));
            }
          } catch { /* browser storage is optional; never recreate a missing record */ }
          draftRevisions.current.set(submittedDraft.key, {});
          if (draftStorageKey.current === submittedDraft.key) writeState({ draft: '' });
        }
      } else if (instruction.kind === 'clarify_document_intent') {
        const prompt = instruction.message;
        writeState(current => ({ ...current, turns: current.turns.map(turn => turn.id === turnId ? { ...turn, pending: false, clarification: prompt } : turn) }));
      } else if (instruction.kind === 'edit_unavailable') {
        const message = instruction.message;
        writeState(current => ({ ...current, turns: current.turns.map(turn => turn.id === turnId ? { ...turn, pending: false, error: message } : turn) }));
      } else {
        if (instruction.editor_registration_id !== registrationId) throw new Error('This edit belongs to an earlier document view. Please send it again from the current editor.');
        assertCurrent();
        pendingRef.current = true;
        pendingRecordRef.current = { request, startedAt: new Date().toISOString() };
        persistDraft(stateRef.current.draft || request, stateRef.current.explicitEdit);
        writeState({ mutationPending: true, routing: false });
        const result = await registrationRef.current!.capability.submitEdit(request, { assertCurrent });
        assertCurrent();
        const canonical = result.history_turn ? documentHistoryTurn(result.history_turn, active.capability) : null;
        writeState(current => ({ ...current, turns: current.turns.filter(turn => turn.id !== turnId && turn.id !== canonical?.id).concat(canonical ? { ...canonical, presentationKey: turnId } : { id: turnId, presentationKey: turnId, request, result }) }));
        pendingRecordRef.current = null;
        setDraft('');
      }
    } catch (error) {
      if (registrationRef.current?.registrationId !== registrationId) return;
      let safeMessage: string | undefined;
      if (error instanceof HomeChatHttpError && error.body && typeof error.body === 'object') {
        const detail = (error.body as { detail?: { phase?: string; session_id?: string } }).detail;
        if (detail?.phase === 'companion_binding' && detail.session_id) { options.onSessionAllocated?.(detail.session_id); safeMessage = 'Your conversation could not be linked to this workflow. Please try again.'; }
      }
      const reloadRequired = (error as { document_reload_required?: boolean })?.document_reload_required === true;
      if (reloadRequired) { safeMessage = 'The changes were saved. Reload the document before continuing.'; writeState({ reloadRequired: true }); }
      const unknown = pendingRef.current && ((error as { request_outcome_unknown?: boolean })?.request_outcome_unknown === true || error instanceof TypeError || (error instanceof Error && /network|fetch|timeout|timed out/i.test(error.message)));
      if (unknown) { writeState({ unknownOutcome: true }); void reloadHistory(); }
      else if (!reloadRequired) { pendingRecordRef.current = null; persistDraft(stateRef.current.draft || request, stateRef.current.explicitEdit); }
      writeState(current => ({ ...current, turns: current.turns.map(turn => turn.id === turnId ? { ...turn, pending: false, unknown, reloadRequired, error: unknown ? 'The edit outcome is unknown. The submitted request may still complete. Review the latest document and History before trying again.' : (safeMessage || toUserFacingError(error)) } : turn) }));
    } finally {
      if (activeRequestRef.current !== turnId) return;
      activeRequestRef.current = null;
      routingRef.current = false;
      // Unknown server outcomes keep navigation guarded until explicitly acknowledged.
      writeState(current => {
        if (!current.unknownOutcome && !current.reloadRequired) pendingRef.current = false;
        return { ...current, routing: false, mutationPending: current.unknownOutcome || current.reloadRequired };
      });
    }
  }, [persistDraft, reloadHistory, setDraft, writeState]);
  const acknowledgeUnknown = useCallback(() => { pendingRef.current = false; pendingRecordRef.current = null; persistDraft(stateRef.current.draft, stateRef.current.explicitEdit); writeState({ unknownOutcome: false, mutationPending: false }); }, [persistDraft, writeState]);
  const context = useMemo(() => ({ ...state, register, update, setScope, setDraft, setDocumentMode, setExplicitEdit, openAssistant, allowNavigation, allowDocumentViewChange, sendMessage, reloadHistory, acknowledgeUnknown }), [state, register, update, setScope, setDraft, setDocumentMode, setExplicitEdit, openAssistant, allowNavigation, allowDocumentViewChange, sendMessage, reloadHistory, acknowledgeUnknown]);
  return <AssistantContext.Provider value={context}>{children}</AssistantContext.Provider>;
}
export const useDocumentAssistant = () => useContext(AssistantContext);
export function useDocumentAssistantCapability(capability: DocumentAssistantCapability | null) {
  const { register, update, scopeUser, mutationPending, openAssistant } = useDocumentAssistant();
  const owner = useRef({});
  const capabilityRef = useRef(capability); capabilityRef.current = capability;
  useLayoutEffect(() => capabilityRef.current?.activeEditor ? register(capabilityRef.current, owner.current) : undefined, [register, scopeUser, capability?.key, capability?.activeEditor]);
  useLayoutEffect(() => { if (capability) update(capability, owner.current); });
  return { isApplying: mutationPending, openAssistant };
}

/** Route setters share the synchronous mutation lock, including editor Back callbacks. */
export function useDocumentNavigationState<T>(initialValue: T): [T, React.Dispatch<React.SetStateAction<T>>] {
  const { allowNavigation } = useDocumentAssistant();
  const [value, setValue] = useState(initialValue);
  const guardedSet = useCallback<React.Dispatch<React.SetStateAction<T>>>((next) => {
    if (allowNavigation()) setValue(next);
  }, [allowNavigation]);
  return [value, guardedSet];
}

/** Internal document views stay mounted until routing has identified a safe response. */
export function useDocumentViewState<T>(initialValue: T): [T, React.Dispatch<React.SetStateAction<T>>] {
  const { allowDocumentViewChange } = useDocumentAssistant();
  const [value, setValue] = useState(initialValue);
  const guardedSet = useCallback<React.Dispatch<React.SetStateAction<T>>>((next) => {
    if (allowDocumentViewChange()) setValue(next);
  }, [allowDocumentViewChange]);
  return [value, guardedSet];
}
